import { useMemo, useState, useEffect, useRef } from 'react';
import { ClipboardList, AlertTriangle, BarChart3, Clock, CheckCircle2, XCircle, Calendar, Flag, Users, Megaphone, BookOpen, Sun, Coffee, Sparkles, User, Cloud, Bell } from 'lucide-react';
import { useHorarioAtual } from '../../api/hooks/useHorarios';
import { useStatusDia } from '../../api/hooks/useChamadas';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { usePlanoAula, getSemanaAno, type PlanoAula } from '../../api/hooks/usePlanoAula';
import { toMin, fmtTimer, cleanName } from './professorUtils';
import '../../components/ui/datatable.css';
import './mobile-dashboard.css';
import { MobileTabBar } from './MobileTabBar';
import { MobileWeekCalendar } from './MobileWeekCalendar';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useUnreadNotifications } from '../../components/notifications/InAppNotificationWatcher';

const OfflineBanner = () => {
  const isOnline = useOnlineStatus();
  if (isOnline) return null;
  
  return (
    <div style={{ background: '#f59e0b', color: 'white', padding: '8px 14px', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, position: 'sticky', top: 0, zIndex: 50 }}>
      <AlertTriangle size={16} />
      Modo Offline. Alterações serão salvas localmente.
    </div>
  );
};

/** Badge da fila offline — componente próprio (hooks no IIFE do pai quebravam React #310 no resize do iframe Chronos). */
function SyncQueueBadge() {
  const [syncCount, setSyncCount] = useState(0);
  useEffect(() => {
    const checkCount = async () => {
      try {
        const { getSyncQueueCount } = await import('../../lib/offline/syncQueue');
        setSyncCount(await getSyncQueueCount());
      } catch {
        /* noop */
      }
    };
    void checkCount();
    const interval = setInterval(checkCount, 5000);
    window.addEventListener('offline-queue-updated', checkCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('offline-queue-updated', checkCount);
    };
  }, []);

  if (syncCount === 0) return null;
  return (
    <div style={{ padding: '4px 8px', borderRadius: 14, fontSize: '0.72rem', fontWeight: 700, background: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', gap: 4 }}>
      <Cloud size={12} /> {syncCount}
    </div>
  );
}
import { MobileAlunosAtencao } from './MobileAlunosAtencao';
import { MobileSplash } from './MobileSplash';
import { MobileDashboardFooter } from './MobileDashboardFooter';
import { ProfessorDashboardDesktop } from './ProfessorDashboardDesktop';

// Hook responsivo
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

/* ================================================================
   PROFESSOR DASHBOARD -- TORRE DE CONTROLE
   ================================================================
   5 estados automaticos:
     A) Aula em Andamento (verde)
     B) Proxima Aula (amarelo)
     C) Pendencias Criticas (vermelho)
     D) Dia Finalizado (azul)
     E) Sem Aulas Hoje (cinza)
   ================================================================ */


// ----------------------------------------------------------------

// ----------------------------------------------------------------

export function ProfessorDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { unreadCount: unreadComunicados, bellPulse } = useUnreadNotifications();
  const { hojeStr: hoje, isSimulation } = useHojeReferencia();
  const { virtualEpochMs } = useSimClock({
    enabled: isSimulation,
    pollMs: isSimulation ? 2_000 : 15_000,
  });

  // Hooks de dados (em simulação, grade/chamadas usam o dia da sessão)
  const { horarioAtual, intervaloAtual, horariosHoje, isLoading: loadingHorarios, refetch: refetchHorarios } = useHorarioAtual(
    user?.id,
    isSimulation ? hoje : undefined,
  );
  const { chamadasDia, isLoading: loadingChamadas, refetch: refetchChamadas } = useStatusDia(user?.id, hoje);

  // Fase 2: Plano de Aula Semanal (semana + ano letivo pelo dia de referência / sim)
  const refDate = useMemo(() => {
    if (hoje && /^\d{4}-\d{2}-\d{2}$/.test(hoje)) return new Date(`${hoje}T12:00:00`);
    return new Date();
  }, [hoje]);
  const [semanaAtual, setSemanaAtual] = useState(() => getSemanaAno(refDate));
  const [anoLetivoAtual, setAnoLetivoAtual] = useState(() => refDate.getFullYear());
  useEffect(() => {
    setSemanaAtual(getSemanaAno(refDate));
    setAnoLetivoAtual(refDate.getFullYear());
  }, [refDate]);
  const { useSemana } = usePlanoAula();
  const { data: planosSemana = [], isLoading: loadingPlanos } = useSemana(user?.id, semanaAtual, anoLetivoAtual);

  // Relógio: na simulação usa virtualEpochMs; fora, tick civil a cada 1s.
  const [wallNow, setWallNow] = useState(() => new Date());
  useEffect(() => {
    if (isSimulation) return;
    let t: ReturnType<typeof setInterval> | null = setInterval(() => setWallNow(new Date()), 1000);
    const onVisibility = () => {
      if (document.hidden) {
        if (t) { clearInterval(t); t = null; }
      } else {
        setWallNow(new Date());
        if (!t) t = setInterval(() => setWallNow(new Date()), 1000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { if (t) clearInterval(t); document.removeEventListener('visibilitychange', onVisibility); };
  }, [isSimulation]);

  const agora = useMemo(() => {
    if (isSimulation && Number.isFinite(virtualEpochMs) && virtualEpochMs > 0) {
      return new Date(virtualEpochMs);
    }
    return wallNow;
  }, [isSimulation, virtualEpochMs, wallNow]);

  // Auto-refetch quando a aula muda (timer chega a 0)
  const prevHorarioId = useRef<string | null>(null);
  useEffect(() => {
    const curId = horarioAtual?.id || null;
    if (prevHorarioId.current !== null && curId !== prevHorarioId.current) {
      // Aula mudou (terminou ou trocou) -- forcar refetch
      refetchHorarios();
      refetchChamadas();
    }
    prevHorarioId.current = curId;
  }, [horarioAtual?.id]);


  const horaAtualStr = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
  const agoraMin = toMin(horaAtualStr);

  // --- Calculos derivados -------------------------------------

  // Todos os slots do dia (aulas + intervalos) ordenados por hora
  const slotsHoje = useMemo(() =>
    horariosHoje.sort((a, b) => (a.horaInicio || '').localeCompare(b.horaInicio || '')),
    [horariosHoje],
  );

  // So aulas (para contadores e logica de chamada)
  const aulasHoje = useMemo(() =>
    slotsHoje.filter((h) => h.tipo === 'AULA'),
    [slotsHoje],
  );

  const statusMap = useMemo(() => {
    const map: Record<string, string> = {};
    chamadasDia.forEach((c) => { if (c.horarioAulaId) map[c.horarioAulaId] = c.status; });
    return map;
  }, [chamadasDia]);

  const chamadasFeitas = chamadasDia.filter((c) => c.status === 'CONCLUIDA').length;
  const totalAulas = aulasHoje.length;

  // Aulas perdidas (passadas sem chamada)
  const perdidas = useMemo(() =>
    aulasHoje.filter((h) => h.horaFim && toMin(h.horaFim) < agoraMin && !statusMap[h.id]),
    [aulasHoje, agoraMin, statusMap],
  );

  // Proxima aula (futura mais proxima)
  const proximaAula = useMemo(() =>
    aulasHoje.find((h) => h.horaInicio && toMin(h.horaInicio) > agoraMin && !statusMap[h.id]),
    [aulasHoje, agoraMin, statusMap],
  );

  // Dia finalizado?
  const ultimaAula = aulasHoje[aulasHoje.length - 1];
  const diaFinalizado = ultimaAula && ultimaAula.horaFim && agoraMin > toMin(ultimaAula.horaFim);

  // Progresso da aula atual (precisao de segundos para barra fluida)
  const agoraSec = agora.getHours() * 3600 + agora.getMinutes() * 60 + agora.getSeconds();
  const aulaProgress = useMemo(() => {
    if (!horarioAtual?.horaInicio || !horarioAtual?.horaFim) return { pct: 0, restSec: 0 };
    const iniSec = toMin(horarioAtual.horaInicio) * 60;
    const fimSec = toMin(horarioAtual.horaFim) * 60;
    const durSec = fimSec - iniSec;
    if (durSec <= 0) return { pct: 0, restSec: 0 };
    const elapsedSec = agoraSec - iniSec;
    const pct = Math.min(100, Math.max(0, (elapsedSec / durSec) * 100));
    const restSec = Math.max(0, fimSec - agoraSec);
    return { pct, restSec };
  }, [horarioAtual, agoraSec]);

  // Timer-based refetch: quando o tempo restante chega a 0, invalidar cache com exponential backoff (P2)
  const refetchCountRef = useRef(0);
  useEffect(() => {
    if (aulaProgress.restSec <= 0 && horarioAtual) {
      if (refetchCountRef.current < 4) {
        // Exponential backoff: 0s, 1s, 2s, 4s
        const delay = refetchCountRef.current === 0 ? 0 : Math.pow(2, refetchCountRef.current - 1) * 1000;
        const t = setTimeout(() => {
          refetchCountRef.current += 1;
          queryClient.invalidateQueries({ queryKey: ['horario-atual'] });
          queryClient.invalidateQueries({ queryKey: ['chamadas-status-dia'] });
        }, delay);
        return () => clearTimeout(t);
      }
    } else if (aulaProgress.restSec > 5) {
      refetchCountRef.current = 0;
    }
  }, [aulaProgress.restSec, horarioAtual]);

  // Minutos ate proxima aula
  const minParaProxima = proximaAula?.horaInicio ? toMin(proximaAula.horaInicio) - agoraMin : 0;

  // % concluido do dia
  const pctDia = totalAulas > 0 ? Math.round((chamadasFeitas / totalAulas) * 100) : 0;

  // Proximas aulas (nao perdidas, nao atuais)
  const futuras = aulasHoje.filter((h) => h.horaInicio && toMin(h.horaInicio) > agoraMin);

  // Aula atual ja teve chamada?
  const chamadaAtualFeita = horarioAtual ? statusMap[horarioAtual.id] === 'CONCLUIDA' : false;

  // Aula expirada (timer chegou a 0 mas backend ainda nao atualizou)
  const aulaExpirada = horarioAtual && aulaProgress.restSec <= 0;

  // --- Determinar ESTADO do heroi ----------------------------

  type HeroState = 'PREPARACAO' | 'AULA_AGORA' | 'CHAMADA_FEITA' | 'PROXIMA' | 'PENDENCIAS' | 'FIM_DIA' | 'SEM_AULAS' | 'INTERVALO';

  const heroState: HeroState = useMemo(() => {
    if (totalAulas === 0) return 'SEM_AULAS';
    // Intervalo ativo
    if (intervaloAtual && !horarioAtual) return 'INTERVALO';
    // Se a aula expirou (timer=0), nao ficar preso em AULA_AGORA/CHAMADA_FEITA
    if (horarioAtual && !chamadaAtualFeita && !aulaExpirada) return 'AULA_AGORA';
    if (horarioAtual && chamadaAtualFeita && !aulaExpirada) return 'CHAMADA_FEITA';
    // Antes da 1a aula do dia inteiro
    const primeiraAula = aulasHoje[0];
    if (primeiraAula && primeiraAula.horaInicio && agoraMin < toMin(primeiraAula.horaInicio)) {
      return 'PREPARACAO';
    }

    // PENDENCIAS tem PRIORIDADE sobre FIM_DIA - se tem chamadas pendentes,
    // o professor precisa ver acao, nunca um card vazio de "dia encerrado"
    if (perdidas.length > 0) return 'PENDENCIAS';
    
    // Proxima aula (ainda ha aulas futuras)
    if (proximaAula) return 'PROXIMA';
    
    // Dia realmente finalizado (sem pendencias, sem proximas)
    return 'FIM_DIA';
  }, [totalAulas, horarioAtual, intervaloAtual, chamadaAtualFeita, diaFinalizado, perdidas, proximaAula, aulaExpirada, agoraMin, aulasHoje]);

  // --- Cores do heroi -----------------------------------------

  const heroColors: Record<HeroState, { bg: string; accent: string }> = {
    PREPARACAO:   { bg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', accent: '#eff6ff' },
    AULA_AGORA:   { bg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', accent: '#eff6ff' },
    CHAMADA_FEITA:{ bg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', accent: '#eff6ff' },
    PROXIMA:      { bg: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)', accent: '#fffbeb' },
    PENDENCIAS:   { bg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', accent: '#fef2f2' },
    FIM_DIA:      { bg: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', accent: '#eef2ff' },
    SEM_AULAS:    { bg: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)', accent: '#f9fafb' },
    INTERVALO:    { bg: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)', accent: '#fffbeb' },
  };

  // Barra de progresso cor
  const barColor = aulaProgress.pct > 85 ? '#ef4444' : aulaProgress.pct > 60 ? '#f59e0b' : '#34d399';

  // Splash screen (mobile only, once per session) — nunca no embed do cockpit
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem('ac_cockpit_embed') === '1' || sessionStorage.getItem('ac_skip_splash') === '1') {
        return false;
      }
      return !sessionStorage.getItem('ac_splash_shown');
    }
    return false;
  });
  const handleSplashFinish = () => {
    setShowSplash(false);
    sessionStorage.setItem('ac_splash_shown', '1');
  };

  // ===== MOBILE LAYOUT (Redesigned) ==============================
  if (isMobile) {
    const chamadaAtual = horarioAtual ? chamadasDia.find(c => c.horarioAulaId === horarioAtual.id) : null;
    const firstName = cleanName(user?.nome || '').split(' ')[0] || 'Professor';
    const hora = agora.getHours();
    const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
    const hasPendencias = perdidas.length > 0;
    const dataLabelMobile =
      hoje && /^\d{4}-\d{2}-\d{2}$/.test(hoje)
        ? new Date(`${hoje}T12:00:00`).toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })
        : agora.toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          });

    return (
      <>
        {showSplash && <MobileSplash onFinish={handleSplashFinish} />}
        <OfflineBanner />
        <div style={{ padding: '0 14px 80px', background: 'var(--bg-main, #f2f2f7)', minHeight: '100vh' }}>
        {/* Header: avatar 48px, saudacao 1.05rem, data 0.78rem */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, paddingBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {user?.fotoUrl ? (
              <img src={user.fotoUrl} alt={firstName}
                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2.5px solid rgba(37,99,235,0.25)', boxShadow: '0 3px 10px rgba(0,0,0,0.12)' }} />
            ) : (
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #2563eb, #60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 10px rgba(37,99,235,0.3)', color: 'white' }}>
                <User size={24} />
              </div>
            )}
            <div>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1.2 }}>{saudacao}, {firstName}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #8e8e93)', marginTop: 2, textTransform: 'capitalize' }}>{dataLabelMobile}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SyncQueueBadge />

            {totalAulas > 0 && (
              <div style={{ padding: '4px 10px', borderRadius: 14, fontSize: '0.72rem', fontWeight: 700, background: chamadasFeitas === totalAulas ? 'rgba(52,211,153,0.15)' : 'rgba(37,99,235,0.1)', color: chamadasFeitas === totalAulas ? '#059669' : '#2563eb' }}>
                {chamadasFeitas}/{totalAulas}
              </div>
            )}

            <button
              type="button"
              aria-label="Notificações"
              onClick={() => navigate('/professor/comunicados')}
              style={{
                position: 'relative',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: bellPulse ? 'scale(1.12)' : 'scale(1)',
                transition: 'transform 0.2s ease',
              }}
            >
              <Bell
                size={22}
                strokeWidth={1.75}
                style={{ color: unreadComunicados > 0 || bellPulse ? '#2563eb' : 'var(--text-muted, #8e8e93)' }}
              />
              {unreadComunicados > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: bellPulse ? '0 0 0 3px rgba(239,68,68,0.35)' : undefined,
                  }}
                >
                  {unreadComunicados > 9 ? '9+' : unreadComunicados}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Hero Card */}
        <style>{`@keyframes heroShimmer { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
@keyframes skeletonPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }`}</style>

        {/* U10: Skeleton loading while data loads */}
        {(loadingHorarios || loadingChamadas) ? (
          <div style={{ borderRadius: 14, padding: '16px 14px', marginBottom: 10, background: 'linear-gradient(135deg, #e2e8f0, #f1f5f9)', overflow: 'hidden' }}>
            <div style={{ height: 14, width: '40%', borderRadius: 6, background: '#cbd5e1', marginBottom: 10, animation: 'skeletonPulse 1.5s ease-in-out infinite' }} />
            <div style={{ height: 10, width: '65%', borderRadius: 4, background: '#cbd5e1', marginBottom: 14, animation: 'skeletonPulse 1.5s ease-in-out infinite 0.2s' }} />
            <div style={{ height: 8, width: '100%', borderRadius: 4, background: '#e2e8f0', marginBottom: 10 }}>
              <div style={{ height: '100%', width: '30%', borderRadius: 4, background: '#cbd5e1', animation: 'skeletonPulse 1.5s ease-in-out infinite 0.4s' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <div style={{ height: 32, flex: 1, borderRadius: 8, background: '#cbd5e1', animation: 'skeletonPulse 1.5s ease-in-out infinite 0.6s' }} />
              <div style={{ height: 32, flex: 1, borderRadius: 8, background: '#cbd5e1', animation: 'skeletonPulse 1.5s ease-in-out infinite 0.8s' }} />
            </div>
          </div>
        ) : (
        <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 40%, #1e3a5f 70%, #3b82f6 100%)', backgroundSize: '200% 200%', animation: 'heroShimmer 8s ease infinite', borderRadius: 14, padding: '12px 14px', marginBottom: 10, color: 'white', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(37,99,235,0.1)', filter: 'blur(20px)' }} />
          <div style={{ position: 'absolute', bottom: -15, left: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(59,130,246,0.08)', filter: 'blur(15px)' }} />

          {heroState === 'AULA_AGORA' && horarioAtual && (
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', boxShadow: '0 0 4px #34d399' }} />
                    <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#34d399', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Em andamento</span>
                  </div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 800 }}>{horarioAtual.materia?.nome || horarioAtual.numeroAula + 'a Aula'}</div>
                  <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.55)' }}>{horarioAtual.turma?.nome} - {horarioAtual.horaInicio} a {horarioAtual.horaFim}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '1px' }}>{fmtTimer(aulaProgress.restSec)}</div>
                  <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>restantes</div>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 3, height: 3, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ width: aulaProgress.pct + '%', height: '100%', background: barColor, borderRadius: 3, transition: 'width 1s linear' }} />
              </div>
              <button onClick={() => navigate('/professor/chamada')} style={{ width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', color: 'white', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ClipboardList size={14} /> Fazer Chamada
              </button>
            </div>
          )}

          {heroState === 'CHAMADA_FEITA' && horarioAtual && (
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 size={16} style={{ color: '#34d399' }} />
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800 }}>{horarioAtual.materia?.nome || horarioAtual.numeroAula + 'a Aula'}</div>
                    <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.5)' }}>{horarioAtual.turma?.nome} - {horarioAtual.horaInicio} a {horarioAtual.horaFim}</div>
                  </div>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>{fmtTimer(aulaProgress.restSec)}</div>
              </div>
              {chamadaAtual && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1, padding: '6px', borderRadius: 8, background: 'rgba(52,211,153,0.12)', textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#34d399' }}>{chamadaAtual.totalPresentes ?? 0}</div>
                    <div style={{ fontSize: '0.55rem', color: 'rgba(52,211,153,0.7)', fontWeight: 600 }}>Presentes</div>
                  </div>
                  <div style={{ flex: 1, padding: '6px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#f87171' }}>{chamadaAtual.totalAusentes ?? 0}</div>
                    <div style={{ fontSize: '0.55rem', color: 'rgba(248,113,113,0.7)', fontWeight: 600 }}>Ausentes</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {heroState === 'PREPARACAO' && aulasHoje[0] && (
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}><Sun size={12} style={{ color: '#fbbf24' }} /><span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#fbbf24', textTransform: 'uppercase' }}>Preparacao</span></div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{'Proxima: '}{aulasHoje[0].materia?.nome || 'Aula'}</div>
                <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.5)' }}>{'as ' + aulasHoje[0].horaInicio}</div>
              </div>
              {(() => { const s = toMin(aulasHoje[0].horaInicio || '07:00') * 60; const f = Math.max(0, s - agoraSec); return <div style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '1px' }}>{fmtTimer(f)}</div>; })()}
            </div>
          )}

          {heroState === 'PROXIMA' && proximaAula && (
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}><Clock size={12} style={{ color: '#fbbf24' }} /><span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#fbbf24', textTransform: 'uppercase' }}>Proxima aula</span></div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{proximaAula.materia?.nome || proximaAula.numeroAula + 'a Aula'}</div>
                <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.5)' }}>{proximaAula.turma?.nome} - {proximaAula.horaInicio}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {(() => { const s = Math.max(0, toMin(proximaAula.horaInicio || '00:00') * 60 - agoraSec); return <div style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '1px' }}>{fmtTimer(s)}</div>; })()}
                <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)' }}>para iniciar</div>
              </div>
            </div>
          )}

          {heroState === 'INTERVALO' && intervaloAtual && (
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}><Coffee size={12} style={{ color: '#fbbf24' }} /><span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#fbbf24', textTransform: 'uppercase' }}>{intervaloAtual.descricao || 'Intervalo'}</span></div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>{intervaloAtual.horaInicio} - {intervaloAtual.horaFim}</div>
              </div>
              {proximaAula && <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>{'Proxima em ' + (toMin(proximaAula.horaInicio || '00:00') - agoraMin) + ' min'}</div>}
            </div>
          )}

          {heroState === 'PENDENCIAS' && (
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}><AlertTriangle size={14} style={{ color: '#f87171' }} /><span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#f87171' }}>{perdidas.length} pendente(s)</span></div>
              {perdidas.slice(0, 2).map(h => (<div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: '0.68rem' }}><span style={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>{h.horaInicio}</span><span style={{ color: 'rgba(255,255,255,0.8)' }}>{h.materia?.nome || h.numeroAula + 'a Aula'}</span></div>))}
              <button onClick={() => navigate('/professor/chamada')} style={{ width: '100%', padding: '8px', borderRadius: 8, border: 'none', marginTop: 6, background: 'rgba(248,113,113,0.2)', color: '#f87171', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>Resolver</button>
            </div>
          )}

          {heroState === 'FIM_DIA' && (
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                  <Sparkles size={12} style={{ color: '#34d399' }} />
                  <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#34d399', textTransform: 'uppercase' }}>Dia completo</span>
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{chamadasFeitas}/{totalAulas} chamadas realizadas</div>
                <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>Todas as chamadas do dia foram concluidas</div>
              </div>
              <Sparkles size={20} style={{ color: 'rgba(52,211,153,0.3)' }} />
            </div>
          )}

          {heroState === 'SEM_AULAS' && (
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}><Calendar size={12} style={{ color: 'rgba(255,255,255,0.4)' }} /><span style={{ fontSize: '0.58rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Hoje</span></div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>Dia livre</div>
                <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)' }}>Sem aulas programadas.</div>
              </div>
              <Calendar size={20} style={{ color: 'rgba(255,255,255,0.1)' }} />
            </div>
          )}
        </div>
        )}

        {/* Calendario Semanal — mostra aulas do dia selecionado */}
        <MobileWeekCalendar aulasHoje={aulasHoje} statusMap={statusMap} />

        {/* Alunos em Atencao (lista inline, sem card) */}
        <MobileAlunosAtencao professorId={user?.id || ''} onClickAluno={(alunoId) => navigate('/professor/alunos/' + alunoId)} />

        {/* Conteudo de engajamento: Dica, Pilares, Frase */}
        <MobileDashboardFooter />

        {/* Bottom Tab Bar estilo Apple */}
        <MobileTabBar hasPendencias={hasPendencias} unreadComunicados={unreadComunicados} />
      </div>
      </>
    );
  }

  // ===== DESKTOP LAYOUT (arquivo separado — não alterar mobile acima) =====
  return (
    <ProfessorDashboardDesktop
      user={{ id: user!.id, nome: user!.nome }}
      loadingHorarios={loadingHorarios}
      loadingChamadas={loadingChamadas}
      heroState={heroState}
      heroColors={heroColors}
      horarioAtual={horarioAtual}
      intervaloAtual={intervaloAtual}
      proximaAula={proximaAula}
      aulasHoje={aulasHoje}
      slotsHoje={slotsHoje}
      aulaProgress={aulaProgress}
      barColor={barColor}
      chamadasDia={chamadasDia}
      statusMap={statusMap}
      perdidas={perdidas}
      futuras={futuras}
      chamadasFeitas={chamadasFeitas}
      totalAulas={totalAulas}
      pctDia={pctDia}
      agoraMin={agoraMin}
      agoraSec={agoraSec}
      hoje={hoje}
      planosSemana={planosSemana as PlanoAula[]}
      loadingPlanos={loadingPlanos}
      semanaAtual={semanaAtual}
      setSemanaAtual={setSemanaAtual}
      anoLetivoAtual={anoLetivoAtual}
    />
  );
}

// --- Componentes extraidos ------------------------------------
// TimelineGrupos  -> ./TimelineGrupos.tsx
// Utilitarios     -> ./professorUtils.ts
